import { AlertTriangle } from "lucide-react";
import type { ReportIndicator } from "@/types/production-report.types";

interface Props {
  indicator: ReportIndicator | null;
}

const MAX_DIAS = 6;

/**
 * Spec 084 (FR-025 a FR-027) — o aviso de DADO SUSPEITO.
 *
 * Ele não muda o indicador, e o texto diz isso: o número continua sendo o que a fábrica registrou.
 * O que o aviso afirma é mais estreito e mais útil — há dias com saída e sem base numa janela em
 * que a entrada não cobre a saída. Dia sem base em janela coberta é estoque entre etapas e nem
 * chega aqui (o servidor já o descarta). A correção é no dado.
 *
 * "Sem base" e "base zerada" aparecem separados de propósito: a primeira costuma ser lançamento que
 * faltou, a segunda um zero digitado onde deveria haver medição, e o que fazer é diferente.
 */
export function SuspiciousDaysNotice({ indicator }: Props) {
  const dias = indicator?.suspiciousDays;
  if (!indicator || !dias) return null;
  const total = dias.absent.length + dias.zero.length;
  if (total === 0) return null;

  return (
    <div
      role="status"
      className="border-rd-warning-500/30 flex flex-wrap items-start gap-2 rounded-md border bg-rd-warning-50 px-3 py-2 text-[12px]"
    >
      <AlertTriangle size={14} aria-hidden className="mt-0.5 text-rd-warning-700" />
      <div className="min-w-0 space-y-0.5">
        <p className="text-text-primary">
          {total} {total === 1 ? "dia" : "dias"} com {indicator.name} sem base, em período em que
          saiu mais do que entrou. O número não foi alterado — o que precisa de conferência é o
          registro desses dias.
        </p>
        {dias.absent.length > 0 ? <Lista titulo="Sem base" dias={dias.absent} /> : null}
        {dias.zero.length > 0 ? <Lista titulo="Base zerada" dias={dias.zero} /> : null}
      </div>
    </div>
  );
}

function Lista({ titulo, dias }: { titulo: string; dias: Array<{ day: string }> }) {
  // Data civil como ela veio: é a mesma chave que a grade de fechamento exibe, e o gestor a usa
  // para achar a linha na planilha dele.
  const mostrados = dias.slice(0, MAX_DIAS).map((d) => d.day);
  const resto = dias.length - mostrados.length;
  return (
    <p className="text-text-secondary">
      <span className="text-text-tertiary">{titulo}:</span> {mostrados.join(", ")}
      {resto > 0 ? ` e mais ${resto}` : ""}
    </p>
  );
}
